import React, { ReactElement, useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import { Container } from '../../components/Animations'
import Radom, { IPaymentLink, Payment } from '../../state/Radom'
import { RADOM_COLORS, PAY_BASE_URL } from '../../util/Constants'
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom'
import TimeText from '../../components/TimeText'
import BreadCrumbs from '../../components/Breadcrumbs'
import {
  ProductThumbnail, PaymentAssetIcon
} from '../../components/PaymentDetails'
import { ExternalLinkText } from '../Events'
import LinkExternal from '../../components/LinkExternal'
import PaymentMethodDisplay from '../../components/PaymentMethodDisplay'
import Table from '../../components/Table'
import { Currencies } from '../../util/Currencies'
import { getMethod } from '../../util/Managed'
import Pagination from '../../components/Pagination'
import { cutString, formatCurrency, trimZeros } from '../../util/Util'
import { Section, SubTitle, SectionContent, SectionItem } from '../../components/Shared'
import { StatusBadge } from '../CheckoutSessions'
import Gate from '../../components/Gate'
import { usePermissions } from '../../util/permissions'

const qaTimestamp = '2026-04-21T00:00:00.000Z'
const qaPaymentLink: IPaymentLink = {
  id: '22222222-2222-4222-8222-222222222222',
  url: `${PAY_BASE_URL}/payment_link/22222222-2222-4222-8222-222222222222`,
  products: [
    {
      id: 'qa-product-launch-plan',
      name: 'Launch plan',
      description: 'One-time onboarding and payment setup package.',
      addOns: [],
      chargingIntervalSeconds: 0,
      currency: 'GBP',
      price: 100,
      quantity: 1,
      sendSubscriptionEmails: true,
      createdAt: qaTimestamp,
      updatedAt: qaTimestamp,
      imageUrl: '',
      isArchived: false
    },
    {
      id: 'qa-product-priority-support',
      name: 'Priority support',
      description: 'Monthly priority support for merchant operations.',
      addOns: [],
      chargingIntervalSeconds: 2592000,
      currency: 'GBP',
      price: 49,
      quantity: 1,
      sendSubscriptionEmails: true,
      createdAt: qaTimestamp,
      updatedAt: qaTimestamp,
      imageUrl: '',
      isArchived: false
    }
  ],
  gateway: {
    managed: {
      methods: [
        { network: 'Ethereum' },
        { network: 'Base' },
        { network: 'Ethereum', token: '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48' },
        { network: 'Base', token: '0x833589fcd6edb6e08f4c7c32d4f71b54bda02913' }
      ]
    }
  },
  customizations: {
    leftPanelColor: 'rgba(0,0,0,1)',
    primaryButtonColor: '#0a2540',
    slantedEdge: false,
    allowDiscountCodes: true,
    allowFreeInputAmount: false
  },
  inputFields: [],
  sendEmailReceipt: true,
  chargeCustomerNetworkFee: true,
  createdAt: new Date(qaTimestamp),
  updatedAt: new Date(qaTimestamp),
  cancelUrl: 'https://radom.com/checkout/cancel',
  successUrl: 'https://radom.com/checkout/success'
}

const qaPayments = [
  {
    id: 'qa-payment-1',
    organizationId: 'qa-org',
    associatedEntityType: 'paymentLinkOrder',
    associatedEntityId: 'qa-payment-link-order-1',
    currency: 'GBP',
    subtotal: 149,
    total: 149,
    network: 'Ethereum',
    token: '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48',
    amount: 186.14,
    networkFee: 0.58,
    conversionRates: [{ from: 'GBP', to: 'USDC', rate: '1.2493' }],
    netAmount: 185.56,
    customerId: 'qa-customer-northstar',
    startedAt: '2026-04-21T09:32:00.000Z',
    completedAt: '2026-04-21T09:34:00.000Z',
    paymentEventId: 'qa-payment-event-link-1'
  },
  {
    id: 'qa-payment-2',
    organizationId: 'qa-org',
    associatedEntityType: 'paymentLinkOrder',
    associatedEntityId: 'qa-payment-link-order-2',
    currency: 'GBP',
    subtotal: 100,
    total: 100,
    network: 'Base',
    token: undefined,
    amount: 0.03242,
    networkFee: 0.00002,
    conversionRates: [{ from: 'GBP', to: 'ETH', rate: '0.0003242' }],
    netAmount: 0.03224,
    customerId: 'qa-customer-acme',
    startedAt: '2026-04-20T14:12:00.000Z',
    completedAt: '2026-04-20T14:15:00.000Z',
    paymentEventId: 'qa-payment-event-link-2'
  }
] as Payment[]

const ViewPaymentLink = observer((): ReactElement => {
  const params = useParams()
  const [searchParams] = useSearchParams()
  const isQaSeed = searchParams.get('qa') === '1'
  const { has } = usePermissions()
  const [paymentLink, setPaymentLink] = useState<IPaymentLink>()
  const [payments, setPayments] = useState<Payment[]>([])
  const [isLoadingPayments, setIsLoadingPayments] = useState(false)
  const navigate = useNavigate()
  const pageSize = 20
  const [curPage, setCurPage] = useState(0)
  const [numPages, setNumPages] = useState(0)

  const onPageClick = (i: number): void => {
    if (isQaSeed) {
      setCurPage(i)
      return
    }

    setIsLoadingPayments(true)
    Radom.listPaymentsByPaymentLinkId(params.paymentLinkId ?? '', pageSize, i * pageSize)
      .then(res => {
        setPayments(res.data)
        setCurPage(i)
        setNumPages(Math.ceil(res.total / pageSize))
      })
      .catch(err => console.error(err))
      .finally(() => setIsLoadingPayments(false))
  }

  useEffect(() => {
    if (!params.paymentLinkId) return

    if (isQaSeed) {
      setPaymentLink(qaPaymentLink)
      setPayments(qaPayments)
      setNumPages(1)
      setIsLoadingPayments(false)
      return
    }

    Radom.getPaymentLink(params.paymentLinkId).then(res => {
      setPaymentLink(res)
    }).catch(err => {
      console.error(err)
    })

    setIsLoadingPayments(true)
    Radom.listPaymentsByPaymentLinkId(params.paymentLinkId, pageSize, curPage * pageSize).then(res => {
      setPayments(res.data)
      setNumPages(Math.ceil(res.total / pageSize))
    }).catch(err => {
      console.error(err)
    }).finally(() => setIsLoadingPayments(false))
  }, [isQaSeed, params.paymentLinkId])

  if (!paymentLink) {
    return <></>
  }

  const paymentCurrency = payments[0]?.currency ?? paymentLink.products[0]?.currency ?? 'USD'
  const usesSingleCurrency = payments.every(p => (p.currency ?? paymentCurrency) === paymentCurrency)
  const totalCollected = payments.reduce((sum, payment) => sum + Number(payment.total ?? 0), 0)
  const latestPayment = payments
    .filter(payment => payment.completedAt || payment.startedAt)
    .sort((a, b) =>
      new Date(b.completedAt ?? b.startedAt).getTime() - new Date(a.completedAt ?? a.startedAt).getTime()
    )[0]

  return <Container style={{ flexDirection: 'column', fontSize: 14, gap: 30 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <BreadCrumbs items={[
        { item: 'Payment Links', to: '/payment_links' },
        { item: paymentLink.id, to: '.' }
      ]} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <Gate permission='WritePaymentLinks'>
          <Link to={`/payment_links/${paymentLink.id}`} style={{ color: RADOM_COLORS.BLUE_MED, textDecoration: 'none' }}>
          Edit configuration
          </Link>
        </Gate>
        <ExternalLinkText href={paymentLink.url || `${PAY_BASE_URL}/payment_link/${paymentLink.id}`} target='_blank'>
          Open hosted page
          <LinkExternal stroke={RADOM_COLORS.BLACK} width={13} />
        </ExternalLinkText>
      </div>
    </div>

    <Section>
      <SubTitle style={{ fontSize: 18 }}>
            Payment link details
      </SubTitle>

      <SectionContent>
        <SectionItem>
          <SubTitle>ID</SubTitle>
          <ExternalLinkText href={paymentLink.url || `${PAY_BASE_URL}/payment_link/${paymentLink.id}`} target='_blank'>
            {paymentLink.id}
            <LinkExternal stroke={RADOM_COLORS.BLACK} width={14} />
          </ExternalLinkText>
        </SectionItem>

        <SectionItem>
          <SubTitle>Status</SubTitle>
          <StatusBadge status={paymentLink.isArchived ? 'cancelled' : 'active'}>
            {paymentLink.isArchived ? 'Archived' : 'Active'}
          </StatusBadge>
        </SectionItem>

        <SectionItem>
          <SubTitle>Created at</SubTitle>
          <div>
            <TimeText textColor={RADOM_COLORS.BLACK} date={new Date(paymentLink.createdAt)} />
          </div>
        </SectionItem>

        {
          paymentLink.customizations?.allowFreeInputAmount &&
                  <SectionItem>
                    <SubTitle>Amount</SubTitle>
                    <div style={{ fontSize: 14 }}>Contact enters amount</div>
                  </SectionItem>
        }

        {
          !paymentLink.customizations?.allowFreeInputAmount &&
          paymentLink.products && paymentLink.products.length > 0 &&
                  <SectionItem>
                    <SubTitle>Products</SubTitle>
                    <div style={{ fontSize: 14 }}>
                      {paymentLink.products.map((item, i) =>
                        <ProductThumbnail key={i} p={item} disableLink={!has('WriteProducts')} />
                      )}
                    </div>
                  </SectionItem>
        }

        {
          !paymentLink.customizations?.allowFreeInputAmount &&
          (!paymentLink.products || paymentLink.products.length === 0) &&
                  <SectionItem>
                    <SubTitle>Products</SubTitle>
                    <div style={{ color: RADOM_COLORS.GRAY_DARKER }}>No products configured</div>
                  </SectionItem>
        }
      </SectionContent>
      <SectionItem>
        <SubTitle>Payment methods</SubTitle>
        <div key="paymentMethods" style={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', padding: '10px 0' }}>
          {
            paymentLink.gateway.managed?.methods.map((method, i) =>
              <PaymentMethodDisplay
                key={i}
                method={method}
                excludeName
                disableTooltip={false} />)
          }
        </div>
      </SectionItem>
    </Section>

    <Section>
      <SubTitle style={{ fontSize: 18 }}>
        Payments received
      </SubTitle>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: 12,
        marginBottom: 20
      }}>
        {[
          { label: 'Payments', value: payments.length.toString() },
          {
            label: 'Volume',
            value: payments.length > 0
              ? usesSingleCurrency ? formatCurrency(totalCollected, paymentCurrency) : 'Mixed currencies'
              : '-'
          },
          {
            label: 'Latest payment',
            value: latestPayment ? <TimeText date={new Date(latestPayment.completedAt ?? latestPayment.startedAt)} /> : '-'
          }
        ].map(item => (
          <div
            key={item.label}
            style={{
              border: `1px solid ${RADOM_COLORS.GRAY_DARK}`,
              borderRadius: 10,
              padding: 16,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              background: RADOM_COLORS.WHITE
            }}>
            <SubTitle>{item.label}</SubTitle>
            <div style={{ fontSize: 18, fontWeight: 500 }}>{item.value}</div>
          </div>
        ))}
      </div>

      <SectionItem>
        <Table
          headers={[
            <span key='payment-id'>Payment ID</span>,
            <span key="customer">Contact</span>,
            <span key="total">Amount</span>,
            <span key="payment">Paid asset</span>,
            <span key="date">Date</span>
          ]}
          items={
            payments.map(d => [
              <div key='1'>
                {cutString(d.paymentEventId, 12)}
              </div>,
              <div key="customer" onClick={e => e.stopPropagation()}>
                {
                  d.customerId
                    ? <Gate permission='ReadCustomers' fallback={<span style={{ color: RADOM_COLORS.GRAY_DARKER }}>-</span>}>
                      <Link to={`/customer/${d.customerId}`} style={{ color: RADOM_COLORS.BLUE_MED }}>
                        {cutString(d.customerId, 10)}
                      </Link>
                    </Gate>
                    : <span style={{ color: RADOM_COLORS.GRAY_DARKER }}>-</span>
                }
              </div>,
              <div key="2" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {Currencies.find(c => c.ticker.toLowerCase() === (d.currency ?? 'USD').toLowerCase())?.icon}
                <span>{trimZeros(d.total?.toString() ?? '')}</span>
              </div>,
              <div key="3" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <PaymentAssetIcon payment={d} />
                <span>{trimZeros(d.amount.toString())}</span>
                {getMethod(d.network, d.token)?.ticker}
              </div>,
              <TimeText key="4" date={new Date(d.completedAt ?? d.startedAt)} />
            ])
          }
          isLoading={isLoadingPayments}
          emptyPlaceholderText="No payments received for this payment link yet"
          onClickRow={(i) => {
            if (!has('ReadEvents')) return
            const id = payments[i].paymentEventId
            navigate(`/payment/${id}`)
          }}
        />
      </SectionItem>
      <Pagination curPage={curPage} numPages={numPages} onPageClick={onPageClick} />
    </Section>
  </Container>
})

export default ViewPaymentLink
